import { HierarchyProps } from '@pelckmans/elementis/components/hierarchy';
import { Cell, CellWithHierarchy } from '@pelckmans/elementis/components/table';
import CalendarIcon from '@pelckmans/elementis/icons/calendar';
import DetailsIcon from '@pelckmans/elementis/icons/details';
import { ComponentProps, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatAssignmentToDate, formatDate } from '../../../../../utils/formatDate';
import { getResultPercentages, isQuantifiable } from '../../../../../utils/results';
import { Session } from '../../api/history';
import AbsoluteScoreCell from '../table/cells/AbsoluteScoreCell';
import PercentageCell from '../table/cells/PercentageCell';
import ResultsCell from '../table/cells/ResultsCell';
import TimeCell from '../table/cells/TimeCell';
import HistoryTable from './HistoryTable';

const BASE_PATH = 'PAGES.RESULTS.SESSION_HISTORY_MODAL';

const renderDate = (session: Session) => {
  if ((session.completedBySystem && session.isZeroScore) || !session.achievedAt) return null;

  return (
    <Cell.Text startAdornment={<CalendarIcon data-testid="calendar-icon" />} title={formatDate(session.achievedAt)} />
  );
};

interface Props extends Pick<ComponentProps<typeof HistoryTable>, 'isAssignment' | 'sessionsData' | 'scorableItem'> {
  onMaterialAccessed: (session: Session) => Promise<void>;
  horizontalScroll: boolean;
}

interface SessionBodyProps {
  session: Session;
  isAssignment: boolean;
  scorableItem: ComponentProps<typeof HistoryTable>['scorableItem'];
  onMaterialAccessed: (session: Session) => Promise<void>;
  horizontalScroll: boolean;
}

function HistoryTableBodyAdaptiveExerciseSession({
  session,
  isAssignment,
  scorableItem,
  onMaterialAccessed,
  horizontalScroll,
}: SessionBodyProps) {
  const { t } = useTranslation();

  const quantifiable = isQuantifiable({
    score: session.score,
    unachievable: session.unachievable,
    status: session.status,
  });

  const [collapsed, setCollapsed] = useState(false);

  const hierarchyProps: HierarchyProps = {
    level: 0,
    collapsed,
    onToggleCollapsed: () => setCollapsed(x => !x),
    collapseButtonLabelText: t(`${BASE_PATH}.COLLAPSE_BUTTON.A11Y_LABEL`),
  };

  return (
    <tbody>
      <tr>
        {isAssignment && session.assignment ? (
          <>
            <CellWithHierarchy
              highlighted
              hierarchyProps={hierarchyProps}
              pillGroupProps={{ 'aria-label': undefined, role: 'group' }}
            >
              <Cell.Pill>
                {t(`${BASE_PATH}.TABLE_VALUES.FROM`, { from: `${formatAssignmentToDate(session.assignment?.from)}` })}
              </Cell.Pill>
              <Cell.Pill>
                {t(`${BASE_PATH}.TABLE_VALUES.TO`, { to: `${formatAssignmentToDate(session.assignment?.to)}` })}
              </Cell.Pill>
            </CellWithHierarchy>
            <Cell highlighted>{renderDate(session)}</Cell>
          </>
        ) : (
          <>
            <CellWithHierarchy highlighted hierarchyProps={hierarchyProps}>
              {renderDate(session)}
            </CellWithHierarchy>
          </>
        )}
        <Cell highlighted>
          <Cell.Text title={t(`${BASE_PATH}.TABLE_ROW.COMPLETE_PHASE`)} />
        </Cell>
        <Cell minWidth="xs" highlighted>
          <ResultsCell
            quantifiable={quantifiable}
            status={session.status}
            phases={session.phases}
            isLastPhase={session.isLastPhase}
            score={session.score}
          />
        </Cell>
        <PercentageCell
          achieved={quantifiable ? session.score.achieved : undefined}
          maximum={quantifiable ? session.score.maximum : undefined}
          highlighted
        />
        <AbsoluteScoreCell scorableItem={scorableItem} score={quantifiable ? session.score : undefined} highlighted />
        <TimeCell duration={session.duration} highlighted />
        <Cell
          buttonGroupProps={{
            'aria-label': t(`${BASE_PATH}.TABLE_VALUES.DETAILS`),
            role: 'group',
            className: 'button-group',
          }}
          className="actions-cell"
          sticky={horizontalScroll}
          highlighted
        >
          <Cell.Button
            onClick={() => onMaterialAccessed(session)}
            startIcon={<DetailsIcon data-testid="details-icon" />}
            prominence="neutral"
            aria-label={t(`${BASE_PATH}.TABLE_VALUES.DETAILS_BUTTON`)}
          />
        </Cell>
      </tr>
      {!collapsed && (
        <>
          {session.phases?.map(phase => (
            <tr key={`${session.session}-${phase.id}`}>
              <CellWithHierarchy hierarchyProps={{ level: 1, hasCollapsibleParent: true }}>
                <CellWithHierarchy.Text title="" />
              </CellWithHierarchy>
              {/* Add extra cell to match the amount of cells, first cell always should have the hierarchy. */}
              {isAssignment && session.assignment && <Cell />}
              <Cell>
                <Cell.Text title={phase.name} />
              </Cell>
              <Cell>
                <Cell.Results {...getResultPercentages(phase.score)} quantifiable showTooltip variant="donut" />
              </Cell>
              <PercentageCell achieved={phase.score.achieved} maximum={phase.score.maximum} />
              <AbsoluteScoreCell scorableItem={scorableItem} score={phase.score} />
              <TimeCell duration={phase.duration} />
              <Cell />
            </tr>
          ))}
          {!session.isLastPhase && (
            <tr>
              <CellWithHierarchy
                colspan={isAssignment && session.assignment ? 8 : 7}
                hierarchyProps={{ level: 1, hasCollapsibleParent: true }}
              >
                <Cell.Message title={t(`${BASE_PATH}.TABLE_ROW.LAST_PHASE`)} />
              </CellWithHierarchy>
            </tr>
          )}
        </>
      )}
    </tbody>
  );
}

export default function HistoryTableBodyAdaptiveExercise({
  scorableItem,
  isAssignment,
  onMaterialAccessed,
  horizontalScroll,
  sessionsData,
}: Props) {
  return sessionsData?.sessions.map((session, i) => (
    <HistoryTableBodyAdaptiveExerciseSession
      key={`${session.session}-${i}`}
      session={session}
      isAssignment={isAssignment}
      scorableItem={scorableItem}
      onMaterialAccessed={onMaterialAccessed}
      horizontalScroll={horizontalScroll}
    />
  ));
}
